/* eslint-disable react-hooks/exhaustive-deps */
import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import VirtualizedFnbCarousel from '@/components/feature/fnb/fnb-carousel/VirtualizedFnbCarousel';
import FnbSearch from '@/components/feature/fnb/fnb-search';
import { ReviewMenuHorizontal } from '@/components/feature/shop';
import { BuildingMenuType } from '@/enums';
import useFnbDataModal from '@/hooks/poi/useFnbDataModal';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore, useFnbStore } from '@/stores';
import type { PoiContentSlide } from '@/types/shop';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FNBPage.module.scss';
import { FloorGuideType } from '@/enums/floor-guide';
import { useNavigate } from 'react-router-dom';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { JAMSIL_MAP_IDS } from '@/constants/app';

const FNBPage = () => {
    const { isHorizontal } = useOrientation();

    const ui = useConfigStore((state) => state.config.ui);
    const server = useConfigStore((state) => state.config.server);

    const { t } = useTranslation();

    const rawFnbContents = useFnbStore((state) => state.rawFnbContents);
    const fnbContents = useFnbStore((state) => state.fnbContents);
    const currentMode = useFnbStore((state) => state.currentMode);
    const initialize = useFnbStore((state) => state.initialize);
    const setTranslationFunction = useFnbStore((state) => state.setTranslationFunction);
    const resetFilters = useFnbStore((state) => state.resetFilters);
    const updateFnbContents = useFnbStore((state) => state.updateFnbContents);
    const mapId = useConfigStore((state) => state?.config?.server?.store?.studioMapId);
    const isJamsil = JAMSIL_MAP_IDS.includes(mapId || '');

    const { isOpen, isLoading, clickedFnbPoi, setIsOpen, setClickedFnbPoi, menuListProps, reviewListProps, poiDetailsProps } = useFnbDataModal();

    const isInitializedRef = useRef(false);
    const prevLanguageRef = useRef(ui.language);

    const { setSelectedPoi, setFloorGuideType } = useFloorGuideStore();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isInitializedRef.current) {
            resetFilters();
            initialize(t);
            isInitializedRef.current = true;
        }
    }, [t, server?.store.buildings]);

    useEffect(() => {
        if (prevLanguageRef.current !== ui.language) {
            setTranslationFunction(t);
            prevLanguageRef.current = ui.language;
        }
    }, [t, ui.language]);

    useEffect(() => {
        if (rawFnbContents.length > 0) {
            updateFnbContents(currentMode);
        }
    }, [rawFnbContents, currentMode]);

    const pageClassname = useMemo(() => {
        return clsx(styles['fnb'], {
            [styles['fnb__vertical']]: !isHorizontal,
            [styles['fnb__horizontal']]: isHorizontal,
            [styles['fnb__contrast']]: ui.contrast,
            [styles['fnb__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['fnb__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
        });
    }, [ui.screenMode, ui.contrast, isHorizontal]);

    const carouselClassname = useMemo(() => {
        return clsx(styles['fnb__carousel'], {
            [styles['fnb__carousel__horizontal']]: isHorizontal,
            [styles['fnb__carousel__vertical']]: !isHorizontal,
            [styles['fnb__carousel__contrast']]: ui.contrast,
            [styles['fnb__carousel__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['fnb__carousel__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
            [styles['fnb__carousel__vertical__empty']]: fnbContents.length === 0 && !isHorizontal,
            [styles['fnb__carousel__vertical__empty__halfmode']]: fnbContents.length === 0 && !isHorizontal && ui.screenMode === 'split',
            [styles['fnb__carousel__horizontal__empty']]: fnbContents.length === 0 && isHorizontal,
            [styles['fnb__carousel__horizontal__empty__halfmode']]: fnbContents.length === 0 && isHorizontal && ui.screenMode === 'split',
        });
    }, [isHorizontal, ui.screenMode, ui.contrast, fnbContents.length]);

    const currentMenu = useMemo(() => {
        const listMenus = server?.buildingMenus?.[ui.language] || [];
        return listMenus.find((menu) => menu.menuType === BuildingMenuType.FNB);
    }, [server?.buildingMenus, ui.language]);

    const onClickPoiContent = useCallback((data: PoiContentSlide) => {
        setClickedFnbPoi(data);
        setIsOpen(true);
        TrackingService.trackPoi(data.data.id);
    }, []);

    const handleGuideStart = () => {
        if (!clickedFnbPoi) return;
        const buildingMapId = clickedFnbPoi.data.studioBuildingId + '_' + clickedFnbPoi.data.contents.cstrCd['ko'];
        setSelectedPoi({ studioFloorID: clickedFnbPoi?.data.studioFloorId, studioPoiId: clickedFnbPoi?.data.studioPoiId, buildingId: buildingMapId });
        setFloorGuideType(FloorGuideType.DIRECTION);
        navigate(PATHS.FLOOR_GUIDE.DIRECTION_GUIDE(clickedFnbPoi?.data.studioFloorId || '', clickedFnbPoi?.data.studioPoiId || ''));
    };

    const handleLocationCheck = () => {
        if (!clickedFnbPoi) return;
        const buildingMapId = clickedFnbPoi.data.studioBuildingId + '_' + clickedFnbPoi.data.contents.cstrCd['ko'];
        setSelectedPoi({ studioFloorID: clickedFnbPoi?.data.studioFloorId, studioPoiId: clickedFnbPoi?.data.studioPoiId, buildingId: buildingMapId });
        setFloorGuideType(FloorGuideType.MARKER);
        navigate(PATHS.FLOOR_GUIDE.CURRENT_LOCATION(clickedFnbPoi?.data.studioFloorId || '', clickedFnbPoi?.data.studioPoiId || ''));
    };

    return (
        <div className={pageClassname}>
            <div
                className={clsx(styles['fnb__header'], {
                    [styles['fnb__header--horizontal']]: isHorizontal,
                    [styles['fnb__header--vertical']]: !isHorizontal,
                    [styles['fnb__header--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                })}
            >
                <ShopFnbFacilityHeader title={currentMenu?.name || ''} description={currentMenu?.description || ''} isHorizontal={isHorizontal} tabIndex={1} isFABPage={true} />
            </div>
            <div className={carouselClassname}>
                {fnbContents.length > 0 ? (
                    <VirtualizedFnbCarousel contents={fnbContents} onClickPoiContent={onClickPoiContent} />
                ) : (
                    <ShopFnbFacilityCarouselEmpty
                        id="fnb-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                    />
                )}
            </div>
            <div
                className={clsx(styles['fnb__search'], {
                    [styles['fnb__search--horizontal']]: isHorizontal,
                    [styles['fnb__search--vertical']]: !isHorizontal,
                })}
            >
                <FnbSearch />
            </div>

            <ReviewMenuHorizontal
                poiDetails={poiDetailsProps}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                menuList={menuListProps}
                reviewList={reviewListProps}
                isLoading={isLoading}
                onGuideStart={handleGuideStart}
                onLocationCheck={handleLocationCheck}
            />
        </div>
    );
};

export default FNBPage;
